import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { DoorOpen, Pencil, Plus, Power, Users } from 'lucide-react'

import { ApiError } from '@/api/client'
import { locationsApi, roomsApi } from '@/api/endpoints'
import type { Room } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { isAdmin } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { StatusBadge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/Modal'
import { Pagination } from '@/components/ui/Pagination'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ToggleChip, Toolbar } from '@/components/ui/Toolbar'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { plural } from '@/lib/format'
import { RoomFormModal } from './RoomFormModal'

const PAGE_SIZE = 20

export function RoomsPage() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { notifySuccess, notifyError } = useToast()
  const canManage = isAdmin(user)

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [locationId, setLocationId] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Room | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [toDeactivate, setToDeactivate] = useState<Room | null>(null)

  const debouncedSearch = useDebouncedValue(search)

  const query = useQuery({
    queryKey: ['rooms', { page, search: debouncedSearch, locationId, includeInactive }],
    queryFn: () =>
      roomsApi.list({
        page,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        location_id: locationId || undefined,
        include_inactive: includeInactive,
      }),
    placeholderData: keepPreviousData,
  })

  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
  })

  const deactivation = useMutation({
    mutationFn: (room: Room) => roomsApi.deactivate(room.id),
    onSuccess: async (_, room) => {
      await queryClient.invalidateQueries({ queryKey: ['rooms'] })
      notifySuccess(`Sala „${room.name}” została dezaktywowana.`)
      setToDeactivate(null)
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się dezaktywować sali.')
      setToDeactivate(null)
    },
  })

  const items = query.data?.items ?? []
  const isEmpty = !query.isPending && items.length === 0
  const hasFilters = search !== '' || locationId !== '' || includeInactive

  return (
    <>
      <PageHeader
        title="Sale"
        description="Sale są zasobem rezerwowanym w grafiku — każda należy do jednego oddziału."
        breadcrumb={['Szkoła', 'Sale']}
        action={
          canManage && (
            <Button
              icon={<Plus size={18} />}
              onClick={() => {
                setEditing(null)
                setIsFormOpen(true)
              }}
            >
              Nowa sala
            </Button>
          )
        }
      />

      <Toolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Szukaj po nazwie lub opisie…"
      >
        <select
          value={locationId}
          onChange={(event) => {
            setLocationId(event.target.value)
            setPage(1)
          }}
          aria-label="Filtr oddziału"
          className="cursor-pointer rounded-full border-2 border-transparent bg-surface-container-low px-4 py-2.5 text-sm font-semibold text-on-surface-variant transition-colors focus:border-primary focus:bg-surface focus:outline-none"
        >
          <option value="">Wszystkie oddziały</option>
          {(locationsQuery.data?.items ?? []).map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>

        <ToggleChip
          checked={includeInactive}
          onChange={(checked) => {
            setIncludeInactive(checked)
            setPage(1)
          }}
          label="Pokaż nieaktywne"
        />
      </Toolbar>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać listy sal. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : isEmpty ? (
          <EmptyState
            icon={<DoorOpen size={28} />}
            title={hasFilters ? 'Brak wyników' : 'Nie ma jeszcze żadnej sali'}
            description={
              hasFilters
                ? 'Zmień kryteria wyszukiwania albo wyczyść filtry.'
                : 'Dodaj sale, aby nauczyciele mogli rezerwować je w grafiku zajęć.'
            }
            action={
              canManage &&
              !hasFilters && (
                <Button icon={<Plus size={18} />} onClick={() => setIsFormOpen(true)}>
                  Nowa sala
                </Button>
              )
            }
          />
        ) : (
          <>
            <DataTable caption="Lista sal lekcyjnych">
              <TableHead
                columns={[
                  'Sala',
                  'Oddział',
                  'Miejsca',
                  'Wyposażenie',
                  'Status',
                  { label: 'Akcje', srOnly: true },
                ]}
              />
              <TableBody>
                {items.map((room) => (
                  <TableRow key={room.id} muted={!room.is_active}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <span
                          className="h-9 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: room.color }}
                          aria-hidden="true"
                        />
                        <div>
                          <Link
                            to={`/sale/${room.id}`}
                            className="font-bold text-on-surface transition-colors hover:text-primary"
                          >
                            {room.name}
                          </Link>
                          {room.description && (
                            <p className="text-xs text-on-surface-variant">{room.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{room.location.name}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5 text-sm">
                        <Users size={14} aria-hidden="true" />
                        {room.capacity}{' '}
                        <span className="text-on-surface-variant">
                          {plural(room.capacity, 'miejsce', 'miejsca', 'miejsc')}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-on-surface-variant">
                        {room.equipment ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge isActive={room.is_active} />
                    </TableCell>
                    <TableCell align="right">
                      <div className="flex justify-end gap-1">
                        <IconButton
                          label={`Edytuj salę ${room.name}`}
                          icon={<Pencil size={16} />}
                          onClick={() => {
                            setEditing(room)
                            setIsFormOpen(true)
                          }}
                          disabled={!canManage}
                        />
                        <IconButton
                          label={`Dezaktywuj salę ${room.name}`}
                          icon={<Power size={16} />}
                          onClick={() => setToDeactivate(room)}
                          disabled={!canManage || !room.is_active}
                          className="hover:bg-error-container hover:text-error"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <EmptyRow colSpan={6} message="Brak sal." />}
              </TableBody>
            </DataTable>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={query.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <RoomFormModal open={isFormOpen} room={editing} onClose={() => setIsFormOpen(false)} />

      <ConfirmDialog
        open={toDeactivate !== null}
        title="Dezaktywować salę?"
        message={
          toDeactivate
            ? `Sala „${toDeactivate.name}” zniknie z listy dostępnych do rezerwacji. Zaplanowane w niej zajęcia i historia grafiku pozostaną nienaruszone.`
            : ''
        }
        confirmLabel="Dezaktywuj"
        isLoading={deactivation.isPending}
        onConfirm={() => toDeactivate && deactivation.mutate(toDeactivate)}
        onClose={() => setToDeactivate(null)}
      />
    </>
  )
}
